import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Camera, Receipt } from "lucide-react";
import { toast } from "sonner";
import { ExpenseFilters, ExpenseTable, ExpenseQuickView, EMPTY_FILTERS } from "@/components/expenses/ExpenseTable";
import { ExpenseFormDialog } from "@/components/expenses/ExpenseFormDialog";
import { ReceiptUploadDialog } from "@/components/expenses/ReceiptUploadDialog";
import { usePermissions } from "@/hooks/usePermissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Expenses() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [data, setData] = useState({ expenses: [], total: 0, count: 0, by_category: [], needs_review_count: 0 });
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [access, setAccess] = useState("full");
  const { can } = usePermissions();
  const [form, setForm] = useState({ open: false, expense: null });
  const [upload, setUpload] = useState(false);
  const [view, setView] = useState(null);
  const [reviewCount, setReviewCount] = useState(0);

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([k, v]) => v && k !== "period"));
    try {
      const { data } = await axios.get(`${API}/expenses`, { params });
      setData(data);
      const all = Object.keys(params).length ? await axios.get(`${API}/expenses`, { params: { status: "needs_review" } }) : { data };
      setReviewCount(all.data.needs_review_count ?? all.data.count ?? 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load expenses"); }
  }, [filters]);

  useEffect(() => {
    axios.get(`${API}/expense-categories`).then((r) => setCategories(r.data)).catch(() => {});
    axios.get(`${API}/vendors`).then((r) => setVendors(r.data)).catch(() => {});
    axios.get(`${API}/customers`).then((r) => setCustomers(r.data)).catch(() => {});
    axios.get(`${API}/expenses/access`).then((r) => setAccess(r.data.access)).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const remove = async (e) => {
    if (!window.confirm(`Delete expense ${e.vendor_name || ""} $${e.total}? Its receipt will be removed too.`)) return;
    try { await axios.delete(`${API}/expenses/${e.id}`); toast.success("Expense deleted"); load(); } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };
  const bulk = async (action, ids, categoryKey) => {
    try {
      if (action === "categorize") await axios.post(`${API}/expenses/bulk-categorize`, { expense_ids: ids, category_key: categoryKey });
      else if (action === "confirm") await axios.post(`${API}/expenses/bulk-confirm`, { expense_ids: ids });
      else for (const id of ids) await axios.delete(`${API}/expenses/${id}`);
      toast.success(`${ids.length} expense(s) ${action === "categorize" ? "categorized" : action === "confirm" ? "confirmed" : "deleted"}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Bulk action failed"); }
  };

  return (
    <div data-testid="expenses-page">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-1 flex items-center gap-3"><Receipt className="w-8 h-8 text-emerald-600" /> Expenses
            {reviewCount > 0 && <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="expenses-needs-review-badge">{reviewCount} need review</Badge>}</h1>
          <p className="text-slate-600">Scan receipts, track spending by Schedule C line, and keep 1099 vendors in order.</p>
        </div>
        <div className="flex gap-2">
          {can("expenses.create") && <Button onClick={() => setUpload(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="upload-receipt-button"><Camera className="w-4 h-4 mr-2" /> Scan receipt</Button>}
          {can("expenses.create") && <Button variant="outline" onClick={() => setForm({ open: true, expense: null })} data-testid="add-expense-button"><Plus className="w-4 h-4 mr-2" /> Add expense</Button>}
        </div>
      </div>
      <div className="space-y-4">
        <ExpenseFilters filters={filters} onChange={setFilters} categories={categories} vendors={vendors} customers={customers} />
        <ExpenseTable data={data} categories={categories} canConfirm={can("expenses.approve")} onEdit={(e) => setForm({ open: true, expense: e })} onDelete={can("expenses.delete") ? remove : null} onView={setView} onBulk={bulk} />
      </div>
      <ExpenseFormDialog open={form.open} onOpenChange={(o) => setForm({ ...form, open: o })} expense={form.expense} categories={categories} vendors={vendors} customers={customers}
        onSaved={load} onVendorCreated={(v) => setVendors((p) => [...p, v])} />
      <ReceiptUploadDialog open={upload} onOpenChange={setUpload} categories={categories} vendors={vendors} customers={customers} onVendorCreated={(v) => setVendors((p) => [...p, v])} onExpenseSaved={() => load()} />
      <ExpenseQuickView expense={view} categories={categories} onOpenChange={setView} />
    </div>
  );
}
